import {SINGLE_CHART_OUTPUT_STEP_DATA_NAME} from 'analysis-tool/AnalysisStepDataNames';
import {getViewForViewReference} from 'analysis-tool/data-view/AnalysisViewUtils';
import {assert} from 'Asserts';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {PipelineGraphQL, assertPipelineStepIsStepType, PipelineViewGraphQL} from 'pipelines/PipelineTypes';
import {
  getDefaultClientIdForView,
  getParentViewsForView,
  resolveLineageStep,
  resolveOutputStepData,
} from 'pipelines/PipelineViewUtils';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

/**
 * Overrides for how to render views in the sidebar.
 */
export interface ViewSidebarOverrides {
  /**
   * Set of view IDs to skip rendering as normal children.
   */
  skipViewIds: Set<string>;
  /**
   * Additional children for parent views outside of the normal view topology, keyed by parent view ID.
   */
  topologyOverrides: Map<string, Array<TopologyOverride>>;
  /**
   * When true, layout views are excluded from child sidebar items (because they are rendered
   * in their own dedicated "Layouts" section).
   */
  excludeLayoutChildViews: boolean;
}

export interface TopologyOverride {
  childViews: Array<PipelineViewGraphQL>;
  // If specified, creates an artificial grouping item in the sidebar, which links to the first child view
  groupingItem?: {
    title: string;
    icon: BenchlingIconName;
  };
  /**
   * When true, the ancestor-skipping DFS is not run for this override's child views.
   * Use this when the override parent is a direct parent of the child views (no intermediate
   * ancestors to hide). This prevents the DFS from incorrectly hiding sibling parent views.
   */
  skipAncestorDFS?: boolean;
}

type TopologyOverrideResult = Pick<ViewSidebarOverrides, 'skipViewIds' | 'topologyOverrides'>;

/**
 * Get regression-specific topology overrides.
 *
 * Groups regression views by their step ID and creates a grouping item (using the
 * regression name from config) that nests all output views (chart, model variables,
 * model output) under the parent view. This mirrors the grouping pattern used for
 * user-defined function steps.
 *
 * For regression views whose parent is a DATASET_VIEW view, skip that parent view and
 * nest the regression views directly under the ancestor.
 */
export function getRegressionTopologyOverrides(analysis: PipelineGraphQL): TopologyOverrideResult {
  const skipViewIds = new Set<string>();
  const topologyOverrides = new Map<string, Array<TopologyOverride>>();

  const regressionViewsByStepId = new Map<string, Array<PipelineViewGraphQL>>();
  analysis.views
    .filter(
      (view) =>
        view.lineage.length > 0 &&
        resolveLineageStep(analysis.steps, view.lineage[0]).stepType === PipelineStepType.REGRESSION
    )
    .forEach((regressionView) => {
      const stepId = regressionView.lineage[0].step.id;
      if (!regressionViewsByStepId.has(stepId)) {
        regressionViewsByStepId.set(stepId, []);
      }
      regressionViewsByStepId.get(stepId)!.push(regressionView);
    });

  regressionViewsByStepId.forEach((regressionViews) => {
    if (regressionViews.length === 0) {
      return;
    }

    // Sort so the chart view comes first — clicking the grouping item selects childViews[0].
    regressionViews.sort((a, b) => {
      const aIsChart = isRegressionChartView(a, analysis);
      const bIsChart = isRegressionChartView(b, analysis);
      return aIsChart === bIsChart ? 0 : aIsChart ? -1 : 1;
    });

    const step = resolveLineageStep(analysis.steps, regressionViews[0].lineage[0]);
    assertPipelineStepIsStepType(PipelineStepType.REGRESSION, 0, step);
    const regressionName = step.config?.name;

    const parentViews = getParentViewsForView(analysis, regressionViews[0]);
    if (parentViews.length === 0) {
      return;
    }

    const [parentView] = parentViews;

    // If the parent is a DATASET_VIEW ("source"), skip it and nest under the ancestor instead.
    const parentIsDatasetView =
      parentView.lineage[0] != null &&
      resolveLineageStep(analysis.steps, parentView.lineage[0]).stepType === PipelineStepType.DATASET_VIEW;

    if (parentIsDatasetView) {
      const [ancestorView] = getParentViewsForView(analysis, parentView);
      // Ancestor view might not exist if the dataset view step was orphaned by deleting its incoming
      // edge via the flowchart.
      if (ancestorView) {
        const ancestorViewId = getDefaultClientIdForView(ancestorView, analysis.steps);
        const existing = topologyOverrides.get(ancestorViewId) ?? [];
        topologyOverrides.set(ancestorViewId, [
          ...existing,
          {
            childViews: regressionViews,
            groupingItem: {
              title: regressionName != null && regressionName.length > 0 ? regressionName : 'Regression',
              icon: 'scatter-plot',
            },
          },
        ]);
      }
    } else {
      const parentViewId = getDefaultClientIdForView(parentView, analysis.steps);
      const existing = topologyOverrides.get(parentViewId) ?? [];
      topologyOverrides.set(parentViewId, [
        ...existing,
        {
          childViews: regressionViews,
          groupingItem: {
            title: regressionName != null && regressionName.length > 0 ? regressionName : 'Regression',
            icon: 'scatter-plot',
          },
          // Regression overrides nest directly under a parent — no intermediate ancestors to skip.
          // skipAncestorDFS prevents the DFS from incorrectly hiding sibling parent views.
          skipAncestorDFS: true,
        },
      ]);
    }
  });

  return {skipViewIds, topologyOverrides};
}

function isRegressionChartView(view: PipelineViewGraphQL, analysis: PipelineGraphQL): boolean {
  const firstNode = view.lineage[0];
  if (firstNode.stepOutputReference == null) {
    return false;
  }
  const outputStepData = resolveOutputStepData(analysis.steps, {
    step: firstNode.step,
    stepOutputReference: firstNode.stepOutputReference,
  });
  return outputStepData?.name === SINGLE_CHART_OUTPUT_STEP_DATA_NAME;
}

/**
 * Get topology overrides for layout views to customize their sidebar placement.
 *
 * If a layout has a sidebarParent config specified, then ensure that the layout is nested directly under the
 * parent. If the layout also has a sidebarGrouping config, then ensure that all layouts with corresponding
 * groupings get grouped together.
 */
export function getLayoutTopologyOverrides(analysis: PipelineGraphQL): TopologyOverrideResult {
  const groupingKeyToEntry: Map<string | null, {parentViewId: string; override: TopologyOverride}> =
    new Map();

  analysis.views.forEach((view) => {
    const lastNode = view.lineage[view.lineage.length - 1];
    const lastStep = resolveLineageStep(analysis.steps, lastNode);

    if (lastStep.stepType !== PipelineStepType.LAYOUT) {
      return;
    }

    const sidebarParent = getLayoutSidebarParentViewId(analysis, view);
    if (sidebarParent == null) {
      return;
    }

    const layoutConfig = lastStep.config as Record<string, unknown> | null;
    assert(layoutConfig != null, 'Expected non-null layout config');
    const sidebarGrouping = layoutConfig.sidebarGrouping as
      | {key: string | null; displayName?: string}
      | undefined;
    const key = sidebarGrouping?.key ?? null;
    if (!groupingKeyToEntry.has(key)) {
      const override: TopologyOverride = {
        childViews: [],
        ...(sidebarGrouping?.displayName
          ? {groupingItem: {title: sidebarGrouping.displayName, icon: 'wave-sine' as BenchlingIconName}}
          : {}),
      };
      groupingKeyToEntry.set(key, {parentViewId: sidebarParent, override});
    }

    groupingKeyToEntry.get(key)!.override.childViews.push(view);
  });

  const topologyOverrides = new Map<string, Array<TopologyOverride>>();
  groupingKeyToEntry.forEach(({parentViewId, override}) => {
    const existing = topologyOverrides.get(parentViewId) ?? [];
    topologyOverrides.set(parentViewId, [...existing, override]);
  });

  return {skipViewIds: new Set(), topologyOverrides};
}

function getLayoutSidebarParentViewId(
  analysis: PipelineGraphQL,
  layoutView: PipelineViewGraphQL
): string | null {
  const layoutStep = resolveLineageStep(analysis.steps, layoutView.lineage[layoutView.lineage.length - 1]);
  assertPipelineStepIsStepType(PipelineStepType.LAYOUT, 0, layoutStep);
  const sidebarParentReference = layoutStep.config?.sidebarParent;
  const sidebarParentEdge = layoutStep.incomingEdges.find(
    (incomingEdge) =>
      incomingEdge.toStepData.name === sidebarParentReference?.inputDataName &&
      incomingEdge.toStepDataIndex === sidebarParentReference?.inputDataIndex
  );
  if (sidebarParentEdge == null) {
    return null;
  }

  const sidebarParentView = getViewForViewReference(analysis, {
    stepId: sidebarParentEdge.fromStep.id,
    outputReference: {
      stepDataName: sidebarParentEdge.fromStepData.name,
      rawDataIndex: sidebarParentEdge.fromStepDataIndex,
    },
  });
  if (sidebarParentView == null) {
    return null;
  }
  return getDefaultClientIdForView(sidebarParentView, analysis.steps);
}

/**
 * Get topology overrides for user-defined function (custom code) views.
 *
 * Groups UDF views by their step ID and creates a "Custom code" grouping item
 * that nests all output views under the parent view. This mirrors the layout grouping
 * pattern used for experiment optimization steps.
 */
export function getUserDefinedFunctionTopologyOverrides(analysis: PipelineGraphQL): TopologyOverrideResult {
  const skipViewIds = new Set<string>();
  const topologyOverrides = new Map<string, Array<TopologyOverride>>();

  const udfViewsByStepId = new Map<string, Array<PipelineViewGraphQL>>();
  analysis.views
    .filter(
      (view) =>
        view.lineage.length > 0 &&
        resolveLineageStep(analysis.steps, view.lineage[0]).stepType ===
          PipelineStepType.USER_DEFINED_FUNCTION
    )
    .forEach((udfView) => {
      const stepId = udfView.lineage[0].step.id;
      if (!udfViewsByStepId.has(stepId)) {
        udfViewsByStepId.set(stepId, []);
      }
      udfViewsByStepId.get(stepId)!.push(udfView);
    });

  udfViewsByStepId.forEach((udfViews) => {
    const parentViews = getParentViewsForView(analysis, udfViews[0]);
    if (parentViews.length === 0) {
      return;
    }

    // Use the first parent view for nesting. When a UDF has multiple parents,
    // this picks one deterministically; all parents remain visible because
    // skipAncestorDFS prevents the DFS from hiding sibling parent views.
    const [parentView] = parentViews;
    const parentViewId = getDefaultClientIdForView(parentView, analysis.steps);
    const step = resolveLineageStep(analysis.steps, udfViews[0].lineage[0]);
    assertPipelineStepIsStepType(PipelineStepType.USER_DEFINED_FUNCTION, 0, step);
    const customName = step.config?.name;
    const existing = topologyOverrides.get(parentViewId) ?? [];
    topologyOverrides.set(parentViewId, [
      ...existing,
      {
        childViews: udfViews,
        groupingItem: {
          title: customName != null && customName.length > 0 ? customName : 'Custom code',
          icon: 'code',
        },
        // UDF overrides nest directly under a parent — no intermediate ancestors to skip.
        // skipAncestorDFS prevents the DFS from hiding sibling parent views.
        skipAncestorDFS: true,
      },
    ]);
  });

  return {skipViewIds, topologyOverrides};
}

/**
 * Generate the viewSidebarOverrides object used to modify the sidebar structure.
 *
 * The overrides we apply are:
 *  1. For regression steps, group all output views (chart, model variables, model output)
 *     under a grouping item using the regression name. For regression views whose parent is a
 *     DATASET_VIEW view, skip that parent view and nest under the ancestor.
 *  2. Get layout topology overrides following the logic in getLayoutTopologyOverrides.
 *  3. For UDF steps, group all output views under a "Custom code" grouping item.
 *  4. Automatically skip all views in the ancestor chain between a parent and child in a topology
 *     override.
 */
export function getViewSidebarOverrides(
  analysis: PipelineGraphQL,
  {excludeLayoutChildViews = false}: {excludeLayoutChildViews?: boolean} = {}
): ViewSidebarOverrides {
  const overrideResults: Array<TopologyOverrideResult> = [
    getRegressionTopologyOverrides(analysis),
    getLayoutTopologyOverrides(analysis),
    getUserDefinedFunctionTopologyOverrides(analysis),
  ];

  const skipViewIds = new Set(overrideResults.flatMap((result) => [...result.skipViewIds]));
  const topologyOverrides = new Map<string, Array<TopologyOverride>>();
  overrideResults.forEach((result) => {
    result.topologyOverrides.forEach((entries, parentViewId) => {
      const existing = topologyOverrides.get(parentViewId) ?? [];
      topologyOverrides.set(parentViewId, [...existing, ...entries]);
    });
  });

  topologyOverrides.forEach((entries, parentViewId) => {
    entries.forEach((entry) => {
      entry.childViews.forEach((childView) => {
        skipViewIds.add(getDefaultClientIdForView(childView, analysis.steps));
      });

      // Run a DFS to hide all views between the parent and child views.
      // Skip this for overrides where the parent is a direct parent of the children
      // (no intermediates to hide), to avoid incorrectly hiding sibling parents.
      if (!entry.skipAncestorDFS) {
        entry.childViews.forEach((childView) => {
          const ancestorViews = getParentViewsForView(analysis, childView);
          while (ancestorViews.length > 0) {
            const ancestorView = ancestorViews.pop()!;
            const ancestorViewId = getDefaultClientIdForView(ancestorView, analysis.steps);
            if (ancestorViewId === parentViewId || skipViewIds.has(ancestorViewId)) {
              continue;
            }

            skipViewIds.add(ancestorViewId);
            ancestorViews.push(...getParentViewsForView(analysis, ancestorView));
          }
        });
      }
    });
  });

  return {
    skipViewIds,
    topologyOverrides,
    excludeLayoutChildViews,
  };
}
